import type { CustomerPriceSearchResult, PriceCheckerQuote } from '@pos/types';

import { getRuntimeConfig } from '../../lib/runtimeConfig';

export interface CustomerConfig {
  liffId: string;
  addFriendUrl: string;
}
export interface CustomerContext {
  customerId: string;
  branches: Array<{ id: string; name: string }>;
}
export class CustomerError extends Error {
  constructor(
    readonly status: number,
    readonly reason?: string,
  ) {
    super('Customer request failed');
  }
}
async function read<T>(
  path: string,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
  const timer = setTimeout(abort, 20000);
  try {
    const { apiBaseUrl } = getRuntimeConfig(import.meta.env);
    const response = await fetch(`${apiBaseUrl}/customer/${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      cache: 'no-store',
      signal: controller.signal,
    });
    const body = await response.json();
    if (!response.ok || !body.ok)
      throw new CustomerError(response.status, body.error?.details?.reason);
    return body.data as T;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}
export const customerApi = {
  search: (
    token: string,
    branchId: string,
    query: string,
    signal?: AbortSignal,
  ) =>
    read<CustomerPriceSearchResult>(
      `search?${new URLSearchParams({ branchId, q: query })}`,
      token,
      signal,
    ),
  config: () => read<CustomerConfig>('config'),
  context: (token: string) => read<CustomerContext>('context', token),
  lookup: (
    token: string,
    branchId: string,
    barcode: string,
    signal?: AbortSignal,
  ) =>
    read<PriceCheckerQuote>(
      `lookup?${new URLSearchParams({ branchId, barcode })}`,
      token,
      signal,
    ),
};
